import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
  SimpleFormField
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import type { IAccountCompletion, IRegistrationView } from '../types';
import {
  fieldLabel,
  missStatement,
  registryLabel,
  revisionLabel,
  unplacedFailure,
  validationErrors
} from './registration-copy';
import { MessageList, QuietAction, StepHeading, slotMessage, useStepAction } from './step-parts';

/**
 * `complete` signed in (`/portal/surveys/add`) — whatever the run's registration collects, and the
 * one step that adds anything to the account. survey-api makes no account on a run bound to a
 * session and ignores an address sent to it, so none is asked for, no mail is caused and no test
 * inbox is drawn (the teams onboarding plan's R7). The anonymous door's completion is page 08
 * (`CreateAccountStep`): the door starts a person's run alone (PFR D4).
 *
 * **The number's fate is said here, because this is where it lands.** survey-api goes from a
 * registry outage straight to this step for a new participant (`ForNewParticipant`) — a new
 * team by way of its route step — so the study's "we could not confirm that number" is not a
 * screen but a notice on this one (R4), and a second `not_found` arrives here too — carrying
 * on with no number at all (an RIA firm's never does since SR-416: its miss stays on the anchor
 * with a notice). Each notice offers "Enter a different number", which is `/revision`: the
 * only way back from this step (R7), and the one survey-api added so that a registrant who
 * notices a typo at the last screen does not have to strand the run and start over. A person's
 * run carries no notice — its numbers moved on with nothing said (PFR §2.2) — and no way back,
 * as page 08 has none.
 *
 * **The fields come from `view.fields` and are judged nowhere.** None is marked required,
 * because what is required is not a per-field fact: a new participant needs *a* name part to
 * be composed from, a participant we already hold — the account's own person, for a number
 * (PFR D16) — needs none. What the server refuses comes back as a `ValidationProblem` and is
 * drawn beside the field it names. A name this build has no label for is still rendered, under
 * its own name: survey-api ignores an unknown one rather than refusing it, and so must the page.
 *
 * **An RIA firm's name is the SEC's once its number matched** (SR-416's B5-12): survey-api
 * replaces `firmName` with ADV Item 1.A's legal name — the view's `registryName` — before it
 * checks anything, so a name typed there would be ignored. The field is drawn read-only holding
 * that name, and sent as shown. `fields` carries no flag for it (survey-api's
 * `RegistrationField.FromRegistry` stays on the server), so it is read off the view — the
 * survey, an `ok` lookup and a `registryName` — by `registryValue`, and nowhere else.
 *
 * **A person's completion refused over the account is not said here.** `several-persons` (PFR
 * D16) and `identifier-already-yours` leave the run no step to take, so the page ends it and draws
 * the chooser under the refusal (Q24).
 */
export function CompleteStep({
  view,
  onComplete,
  onRevise
}: {
  view: IRegistrationView;
  onComplete: (completion: IAccountCompletion) => Promise<void>;
  onRevise: () => Promise<void>;
}) {
  const [fields, setFields] = React.useState<Record<string, string>>(() =>
    registeredValues(view)
  );
  const { pending, error, run } = useStepAction();

  const problems = validationErrors(error);
  const failure = unplacedFailure(
    error,
    new Set(view.fields),
    'Could not add the survey to your account.'
  );
  const person = view.numbers !== null;
  const notice = person ? null : numberNotice(view);
  const revise = () => void run(onRevise);

  return (
    <>
      <StepHeading
        title="Add it to your account"
        lede={
          'Last step — the name we publish under. It is added to the account you are signed ' +
          'in with, so there is no new sign-in to set up.'
        }
      />

      {notice && (
        <Alert className="mb-5">
          <AlertTitle>{notice.title}</AlertTitle>
          <AlertDescription>
            <p>{notice.body}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              disabled={pending}
              onClick={revise}
            >
              Enter a different number
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <form
        noValidate
        className="grid gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          void run(() => onComplete({ fields }));
        }}
      >
        {view.fields.map((name) => {
          const filed = registryValue(view, name) !== null;
          const id = `registration-field-${name}`;
          return (
            <SimpleFormField
              key={name}
              label={fieldLabel(name)}
              htmlFor={id}
              error={slotMessage(problems[name])}
            >
              <Input
                id={id}
                value={fields[name] ?? ''}
                onChange={(event) =>
                  setFields((current) => ({ ...current, [name]: event.target.value }))
                }
                readOnly={filed}
                aria-describedby={filed ? `${id}-filed` : undefined}
                aria-invalid={!!problems[name]}
              />
              {filed && (
                <p id={`${id}-filed`} className="text-sm text-muted-foreground">
                  The firm’s legal name as filed with the SEC (Form ADV, Item 1.A). It is the name
                  the firm registers under.
                </p>
              )}
              <MessageList messages={problems[name]} />
            </SimpleFormField>
          );
        })}

        {failure && (
          <p role="alert" className="text-sm font-medium text-destructive">
            {failure}
          </p>
        )}

        <Button type="submit" disabled={pending} className="w-full">
          {pending ? 'Adding…' : 'Add to my account'}
        </Button>

        {/* Where no notice already offers it: the way back is the same request either way. */}
        {!notice && !person && (
          <QuietAction label={revisionLabel(view)} disabled={pending} onClick={revise} />
        )}
      </form>
    </>
  );
}

/**
 * What became of the number, for every answer but a confirmed match — which needs no
 * saying. Read off `lookup`, `redirected` and `displayName` as the server set them; which
 * branch put the run here was decided there, and this only says what that branch means for
 * the account. `displayName` is the linking branch: survey-api sends a number it already
 * holds to the prompt whatever the registry said, and a yes links the account to the record
 * that holds it — so a miss there drops nothing, and must not say it does.
 */
function numberNotice(view: IRegistrationView): Nullable<{ title: string; body: string }> {
  const registry = registryLabel(view.survey ?? '');
  if (view.lookup === 'error') {
    return {
      title: 'We could not confirm that number',
      body:
        `We could not check that number with ${registry}, so it stays unconfirmed. You can ` +
        'carry on with it, or enter a different number.'
    };
  }
  if (view.lookup === 'not_found') {
    // "No match" rather than "no record": an exempt reporting adviser's number is one the SEC
    // holds, and matches nothing only because survey-api matches registered advisers alone.
    return { title: 'No match for that number', body: missConsequence(view) };
  }
  return null;
}

/** What a registry miss means for the account: redirected, linked or dropped, in that order. */
function missConsequence(view: IRegistrationView): string {
  const missed = missStatement(view.survey ?? '');
  // An API before SR-416 carried RIA_FIRM's second miss here, redirected to TEAMS; no SR-416 run
  // reaches it — a firm's miss stays on the anchor, and a team's redirect is its own step.
  if (view.redirected) return `${missed}, so this account will not start the survey you chose.`;
  if (view.displayName !== null) {
    return (
      `${missed}. It is on the record you confirmed, and your account will be linked to ` +
      'that record.'
    );
  }
  return `${missed}, so the survey will be added to your account without it.`;
}

/**
 * The value a registry gives a field, which the registrant cannot change — RIA_FIRM's
 * `firmName` after an `ok`, ADV's legal name (survey-api's `RegistrationField.FromRegistry`, the
 * one such field). Null for every other field, and wherever the registry named nobody.
 */
function registryValue(view: IRegistrationView, name: string): Nullable<string> {
  return view.survey === 'RIA_FIRM' &&
    name === 'firmName' &&
    view.lookup === 'ok' &&
    view.registryName !== null
    ? view.registryName
    : null;
}

/** The fields a registry fills before anything is typed. */
function registeredValues(view: IRegistrationView): Record<string, string> {
  const values: Record<string, string> = {};
  for (const name of view.fields) {
    const value = registryValue(view, name);
    if (value !== null) values[name] = value;
  }
  return values;
}
